const GWP_STORAGE_KEY = "gwp_eligibility";
const GWP_REDIRECT_KEY = "gwp_checkout_redirect";
const GWP_LEFT_KEY = "gwp_checkout_left";
const GWP_SKIP_CHECKOUT_KEY = "gwp_skip_checkout_variants";
const GWP_TYPE_CONFIG = {
  relative_gwp: { should_add_to_checkout: false },
};

if (!customElements.get("custom-gwp")) {
  customElements.define(
    "custom-gwp",
    class CustomGWP extends HTMLElement {
      #gwpSectionFetch = null;
      #gwpDrawerObserver = null;
      #gwpRenderFrame = null;

      constructor() {
        super();
        this.#handleCheckoutRedirect();
        this.#initialize();
        window.addEventListener("pageshow", (event) => {
          if (event.persisted) this.#initialize();
        });
      }

      get customerTags() {
        try {
          const tags = JSON.parse(this.getAttribute("data-customer-tags") || "[]");
          return Array.isArray(tags) ? tags : [];
        } catch (error) {
          console.error("custom-gwp customer tags parse failed:", error);
          return [];
        }
      }

      get quantityCountText() {
        return this.getAttribute("data-quantity-count-text") || "Qty";
      }

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      #initialize() {
        const onOutboundPermalink = this.#consumeCheckoutRedirectMarker();
        const leftForCheckout = sessionStorage.getItem(GWP_LEFT_KEY) === "1";
        if (leftForCheckout && !onOutboundPermalink) {
          this.#handleReturnFromCheckout();
          return;
        }
        this.#handleNormalLoad(onOutboundPermalink);
      }

      async #handleReturnFromCheckout() {
        const cartBubble = document.querySelector("#cart-icon-bubble");
        cartBubble?.classList.add("hidden");
        this.#closeCartDrawer();
        const cleaned = await this.#cleanupGwpFromCart();
        if (cleaned) this.#removeRenderedGwpLines();
        sessionStorage.removeItem(GWP_LEFT_KEY);
        this.#restoreCartUiAndRender(cartBubble);
      }

      async #handleNormalLoad(skippedCleanup) {
        const cleaned = skippedCleanup ? false : await this.#cleanupGwpFromCart();
        if (cleaned) this.#removeRenderedGwpLines();
        await this.#reconcileOnPageLoad();
        this.#setupCartDrawerObserver();
        this.#renderGwpInCartDrawer();
      }

      #closeCartDrawer() {
        const cartDrawer = document.querySelector("cart-drawer");
        if (!cartDrawer) return;
        if (typeof cartDrawer.close === "function") {
          cartDrawer.close();
        } else {
          customElements.whenDefined("cart-drawer").then(() => cartDrawer.close());
        }
      }

      #restoreCartUiAndRender(cartBubble) {
        cartBubble?.classList.remove("hidden");
        this.#setupCartDrawerObserver();
        this.#renderGwpInCartDrawer();
      }

      async #reconcileOnPageLoad() {
        try {
          const latestData = await this.#handlePullNewestData();
          const gwpConditionData = this.#handleProcessConditions(latestData);
          const { gwpItems, skipCheckout } = this.#parseGwpInfo(gwpConditionData);
          this.#handleStoreGwp(gwpItems, skipCheckout);
        } catch (error) {
          console.error("custom-gwp page-load reconcile failed:", error);
        }
      }

      #replaceNodesFromHtml(html, selectors) {
        selectors.forEach((selector) => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      }

      #rerenderCartDrawerFromSections(cartData) {
        const sectionHtml = cartData?.sections?.["cart-drawer"];
        if (!sectionHtml) return;
        const html = new DOMParser().parseFromString(sectionHtml, "text/html");
        this.#replaceNodesFromHtml(html, ["cart-drawer-items", ".cart-drawer__footer"]);
      }

      #typeAddsToCheckout(type) {
        return GWP_TYPE_CONFIG[type]?.should_add_to_checkout ?? true;
      }

      #parseGwpInfo(data) {
        const gwpItems = {};
        const addable = new Set();
        const skipCandidates = new Set();
        data.forEach((item) => {
          const addsToCheckout = this.#typeAddsToCheckout(item.type);
          item.gwp_variant_ids.forEach((variantId, index) => {
            const key = String(variantId);
            const qty = parseInt(item.quantities[index], 10) || 1;
            gwpItems[key] = (gwpItems[key] || 0) + qty;
            if (addsToCheckout) addable.add(key);
            else skipCandidates.add(key);
          });
        });
        const skipCheckout = [...skipCandidates].filter((key) => !addable.has(key));
        return { gwpItems, skipCheckout };
      }

      #lastAssignedCheck(item, prefix) {
        const tag = this.customerTags.find((t) => t.includes(`last_assigned_gwp-${prefix}:${item.value}|`));
        const lastAssignedDate = tag?.split("|")?.pop();
        return lastAssignedDate ? lastAssignedDate < item.start_date || lastAssignedDate > item.end_date : true;
      }

      #handleGetGwpType(initData = {}) {
        const cartData = initData.cartData;
        const nonGiftItems = (cartData.items || []).filter(cartItem => cartItem.properties?._gwp !== "true");

        return {
          collection: (item) => {
            if (!nonGiftItems.length) return false;
            const hasCollection = Object.entries(initData.collectionData).some(([, collections]) => collections.includes(item.value));
            return hasCollection && this.#lastAssignedCheck(item, "collection");
          },
          customer_tag: (item) => {
            if (!nonGiftItems.length) return false;
            const hasTag = this.customerTags.some(tag => tag.includes(item.value));
            return hasTag && this.#lastAssignedCheck(item, "customer_tag");
          },
          purchase_amount: (item) => {
            if (!nonGiftItems.length) return false;
            const subtotalMajor = cartData.total_price / 100;
            // value: [greater_than_price, less_than_price]
            const [g, l] = Array.isArray(item.value) ? item.value : [null, null];
            const minMatch = g == null || subtotalMajor >= g;
            const maxMatch = l == null || subtotalMajor < l;
            return minMatch && maxMatch;
          },
          sample_gift: () => nonGiftItems.length > 0,
          first_time_buyer: () => nonGiftItems.length > 0,
          relative_gwp: (item) => {
            if (!nonGiftItems.length) return false;
            return nonGiftItems.some((cartItem) => String(cartItem.variant_id) === String(item.value));
          },
          default: (item) => false
        }
      }

      #fetchGwpSectionViaIndex() {
        const sectionId = this.sectionId;
        if (!sectionId) return Promise.resolve(null);
        if (this.#gwpSectionFetch) return this.#gwpSectionFetch;
        this.#gwpSectionFetch = fetch(`/?sections=${encodeURIComponent(sectionId)}`, {
          headers: { Accept: "application/json" },
          // credentials: "same-origin",
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => (data && typeof data[sectionId] === "string" ? data[sectionId] : null))
          .catch(() => null)
          .finally(() => {
            this.#gwpSectionFetch = null;
          });
        return this.#gwpSectionFetch;
      }

      #parseGwpSectionHtml(sectionHtml) {
        if (!sectionHtml) return null;
        const html = new DOMParser().parseFromString(sectionHtml, "text/html");
        const refetchData = html.querySelector("#custom-gwp-refetch-data");
        if (!refetchData) return null;
        const target = this.querySelector("#custom-gwp-refetch-data");
        if (target) target.innerHTML = refetchData.innerHTML;
        return this.#parseGwpScripts(html);
      }

      async #handlePullNewestData(cartData) {
        try {
          const indexHtml = await this.#fetchGwpSectionViaIndex();
          const parsed = this.#parseGwpSectionHtml(indexHtml);
          if (parsed) return parsed;
        } catch (error) {
          console.error("custom-gwp index refetch failed:", error);
        }
        const sectionHtml = cartData?.sections?.[this.sectionId];
        if (!sectionHtml) return this.#readEmbeddedData();

        const parsed = this.#parseGwpSectionHtml(sectionHtml);
        return parsed || this.#readEmbeddedData();
      }

      #handleProcessConditions(data = {}) {
        const types = this.#handleGetGwpType(data);
        return data.initGwpItems
          .map(item => ({
            ...item,
            is_matched: (types[item.type] || types.default)(item)
          }))
          .filter(item => item.is_matched)
      }

      #handleStoreGwp(gwpItems, skipCheckout = []) {
        if (!Object.keys(gwpItems).length) {
          localStorage.removeItem(GWP_STORAGE_KEY);
          localStorage.removeItem(GWP_SKIP_CHECKOUT_KEY);
          return;
        }
        localStorage.setItem(GWP_STORAGE_KEY, JSON.stringify(gwpItems));
        localStorage.setItem(GWP_SKIP_CHECKOUT_KEY, JSON.stringify(skipCheckout || []));
      }

      #buildCheckoutPermalink() {
        try {
          const gwpEntries = this.#checkoutGwpEntries();
          if (!gwpEntries.length) return null;

          const gwpVariantIds = new Set(gwpEntries.map(([id]) => String(id)));
          const cartEntries = (this.#readCartData().items || [])
            .filter((item) => item.properties?._gwp !== "true" && !gwpVariantIds.has(String(item.variant_id)))
            .map((item) => [String(item.variant_id), item.quantity]);

          const entries = [...cartEntries, ...gwpEntries];
          if (!entries.length) return null;
          const basePath = window.routes.cart_url.replace(/\/$/, "");
          return `${window.location.origin}${basePath}/${entries.map(([id, qty]) => `${id}:${qty}`).join(",")}`;
        } catch (error) {
          console.error("custom-gwp build permalink failed:", error);
          return null;
        }
      }

      #handleCheckoutRedirect() {
        document.addEventListener("click", async (event) => {
          const checkoutButton = event.target.closest("#CartDrawer-Checkout, #checkout");
          if (!checkoutButton) return;
          sessionStorage.setItem(GWP_LEFT_KEY, "1");
          if (!this.#isGwpPending()) return;
          const permalink = this.#buildCheckoutPermalink();
          if (!permalink) return;
          event.preventDefault();
          sessionStorage.setItem(GWP_REDIRECT_KEY, "1");
          window.location.href = permalink;
        });
      }

      #isGwpPending() {
        try {
          const entries = this.#checkoutGwpEntries();
          if (!entries.length) return false;
          const inCartVariants = this.#cartVariantIdSet();
          return entries.some(([variantId]) => !inCartVariants.has(variantId));
        } catch (error) {
          return false;
        }
      }

      #consumeCheckoutRedirectMarker() {
        const justRedirected = sessionStorage.getItem(GWP_REDIRECT_KEY) === "1";
        if (!justRedirected) return false;
        sessionStorage.removeItem(GWP_REDIRECT_KEY);
        const cartPath = (window.routes?.cart_url || "/cart").replace(/\/+$/, "");
        const path = window.location.pathname.replace(/\/+$/, "");
        return path === cartPath || path.startsWith(`${cartPath}/`);
      }

      #isGwpLine(item, gwpItems, gwpVariantIds) {
        return (
          item.properties?._gwp === "true" ||
          (item.line_price === 0 &&
            (gwpItems[String(item.variant_id)] != null || gwpVariantIds.has(String(item.variant_id))))
        );
      }

      async #updateCart(updates, sections) {
        const body = JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        });
        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        return response.ok ? response.json() : null;
      }

      async #cleanupGwpFromCart() {
        try {
          const gwpItems = this.#storedGwp();
          const gwpVariantIds = new Set(Object.keys(this.#readGwpProductData()));
          const cartData = this.#readCartData();
          const updates = {};
          let hasNormalItems = false;
          (cartData.items || []).forEach((item) => {
            if (this.#isGwpLine(item, gwpItems, gwpVariantIds)) {
              updates[item.key] = 0;
            } else {
              hasNormalItems = true;
            }
          });

          if (Object.keys(updates).length) {
            const updatedCart = await this.#updateCart(updates, ["cart-drawer"]);
            if (updatedCart) {
              this.#setCartData(updatedCart);
              this.#rerenderCartDrawerFromSections(updatedCart);
              hasNormalItems = (updatedCart.items || []).some(
                (item) => !this.#isGwpLine(item, gwpItems, gwpVariantIds)
              );
            }
          }

          if (!hasNormalItems) {
            localStorage.removeItem(GWP_STORAGE_KEY);
            localStorage.removeItem(GWP_SKIP_CHECKOUT_KEY);
          }

          return Object.keys(updates).length > 0;
        } catch (error) {
          console.error("custom-gwp cleanup failed:", error);
          return false;
        }
      }

      #setCartData(cartData) {
        const script = this.querySelector("#custom-gwp-cart");
        if (script && cartData) script.textContent = JSON.stringify(cartData);
      }

      #removeRenderedGwpLines() {
        const list = document.querySelector("#CartDrawer .cart-items");
        if (!list) return;
        const gwpItems = this.#storedGwp();
        const gwpVariantIds = new Set([
          ...Object.keys(this.#readGwpProductData()),
          ...Object.keys(gwpItems),
        ]);
        list.querySelectorAll("li.cart-item:not([data-gwp-injected])").forEach((li) => {
          const liVariantId = li.dataset.gwpVariantId;
          const inputVariantId = li.querySelector(".quantity__input")?.dataset?.quantityVariantId;
          const variantId = liVariantId || inputVariantId;
          if (variantId && gwpVariantIds.has(String(variantId))) li.remove();
        });
      }

      #readJsonScript(id, fallback = "{}") {
        return JSON.parse(this.querySelector(`#${id}`)?.textContent || fallback);
      }

      #readGwpProductData() {
        try {
          return this.#readJsonScript("custom-gwp-product-data");
        } catch (error) {
          console.error("custom-gwp product data parse failed:", error);
          return {};
        }
      }

      #readCartData() {
        return this.#readJsonScript("custom-gwp-cart");
      }

      #parseGwpScripts(root) {
        const parse = (id, fallback) => JSON.parse(root.querySelector(`#${id}`)?.textContent || fallback);
        return {
          cartData: parse("custom-gwp-cart", "{}"),
          initGwpItems: parse("custom-gwp-info", "[]"),
          collectionData: parse("custom-gwp-collection", "{}"),
        };
      }

      #readEmbeddedData() {
        return this.#parseGwpScripts(this);
      }

      #storedGwp() {
        return JSON.parse(localStorage.getItem(GWP_STORAGE_KEY) || "{}");
      }

      #storedGwpEntries() {
        return Object.entries(this.#storedGwp()).filter(([, qty]) => parseInt(qty, 10) > 0);
      }

      #storedGwpSkipCheckout() {
        try {
          const ids = JSON.parse(localStorage.getItem(GWP_SKIP_CHECKOUT_KEY) || "[]");
          return new Set((Array.isArray(ids) ? ids : []).map(String));
        } catch (error) {
          return new Set();
        }
      }

      #checkoutGwpEntries() {
        const skip = this.#storedGwpSkipCheckout();
        return this.#storedGwpEntries().filter(([variantId]) => !skip.has(String(variantId)));
      }

      #cartVariantIdSet() {
        return new Set((this.#readCartData().items || []).map((item) => String(item.variant_id)));
      }

      #setupCartDrawerObserver() {
        if (this.#gwpDrawerObserver) return;
        const drawer = document.getElementById("CartDrawer");
        if (!drawer) return;
        this.#gwpDrawerObserver = new MutationObserver((mutations) => {
          const relevant = mutations.some((m) =>
            [...m.addedNodes, ...m.removedNodes].some(
              (node) => node.nodeType === 1 && !node.hasAttribute("data-gwp-injected")
            )
          );
          if (!relevant) return;
          if (this.#gwpRenderFrame) cancelAnimationFrame(this.#gwpRenderFrame);
          this.#gwpRenderFrame = requestAnimationFrame(() => this.#renderGwpInCartDrawer());
        });
        this.#gwpDrawerObserver.observe(drawer, { childList: true, subtree: true });
      }

      #renderGwpInCartDrawer() {
        try {
          const entries = this.#storedGwpEntries();
          const list = document.querySelector("#CartDrawer .cart-items");
          if (!list) {
            this.#syncCartIconBubble();
            return;
          }
          if (!entries.length) {
            list.querySelectorAll("[data-gwp-injected]").forEach((el) => el.remove());
            this.#syncCartIconBubble();
            return;
          }

          const productData = this.#readGwpProductData();
          const inCartVariants = this.#cartVariantIdSet();
          const entryMap = new Map(entries.filter(([variantId]) => !inCartVariants.has(variantId)));

          list.querySelectorAll("[data-gwp-injected]").forEach((el) => {
            if (!entryMap.has(el.dataset.gwpVariantId)) el.remove();
          });

          entryMap.forEach((qty, variantId) => {
            this.#renderGwpItem(list, productData, variantId, qty);
          });
          this.#syncCartIconBubble();
        } catch (error) {
          console.error("custom-gwp render drawer failed:", error);
        }
      }

      #syncCartIconBubble() {
        try {
          const list = document.querySelector("#CartDrawer .cart-items");
          let baseCount = 0;
          let gwpCount = 0;
          if (list) {
            list.querySelectorAll(":scope > li:not([data-gwp-injected])").forEach((li) => {
              const qtyEl = li.querySelector(".quantity__input");
              baseCount += parseInt(qtyEl?.textContent || qtyEl?.value, 10) || 0;
            });
            list.querySelectorAll("[data-gwp-injected]").forEach((li) => {
              gwpCount += parseInt(li.dataset.gwpQuantity, 10) || 0;
            });
          }
          const total = baseCount + gwpCount;

          const bubble = document.querySelector("#cart-icon-bubble .cart-count-bubble");
          if (bubble) {
            bubble.classList.toggle("hidden", total === 0);
            const countSpan = bubble.querySelector('span[aria-hidden="true"]');
            if (countSpan) countSpan.textContent = total >= 100 ? "" : String(total);
            const visuallyHidden = bubble.querySelector(".visually-hidden");
            if (visuallyHidden) visuallyHidden.textContent = String(total);
          }

          const headingCount = document.querySelector("#CartDrawer .drawer__heading-count");
          if (headingCount) headingCount.textContent = String(total);
        } catch (error) {
          console.error("custom-gwp sync cart count failed:", error);
        }
      }

      #renderGwpItem(list, productData, variantId, qty) {
        let el = list.querySelector(`[data-gwp-injected][data-gwp-variant-id="${variantId}"]`);
        if (!el) {
          const product = productData[variantId] || {};
          const title = this.#escapeHtml(product.title || "");
          const image = product.image ? this.#escapeHtml(product.image) : "";
          const alt = this.#escapeHtml(product.alt || title);
          const imageHeight = product.aspect_ratio ? Math.ceil(150 / product.aspect_ratio) : 150;
          const template = `
            <li class="cart-item" data-gwp-injected data-gwp-variant-id="${variantId}" data-gwp-quantity="${qty}" id="CartDrawer-Item-gwp-${variantId}">
              <div class="cart-item__wrapper">
                <div class="cart-item__media" headers="CartDrawer-ColumnProductImage">
                  ${image
                      ? `<img class="cart-item__image" src="${image}" alt="${alt}" loading="lazy" width="150" height="${imageHeight}">`
                      : ""
                    }
                </div>
                <div class="cart-item__details" headers="CartDrawer-ColumnProduct">
                  <div class="cart-item__group">${this.quantityCountText}:&nbsp;${qty}</div>
                  <span class="ui-label ui-label--gift ui-label--small ui-label--square">GIFT</span>
                  <span class="cart-item__name">${title}</span>
                </div>
              </div>
            </li>`;

          const wrapperEl = document.createElement("div");
          wrapperEl.innerHTML = template.trim();
          el = wrapperEl.firstElementChild;
          list.appendChild(el);
        }

        el.dataset.gwpQuantity = qty;
        const qtyValueEl = el.querySelector(".quantity__input");
        if (qtyValueEl) qtyValueEl.textContent = qty;
      }

      #escapeHtml(value = "") {
        return String(value).replace(/[&<>"']/g, (char) => ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[char]));
      }

      async handleProcessAutoAssignGwp(sectionIds, sectionsUrl, rawCartData) {
        try {
          const latestData = await this.#handlePullNewestData(rawCartData);
          const gwpConditionData = this.#handleProcessConditions(latestData);
          const { gwpItems, skipCheckout } = this.#parseGwpInfo(gwpConditionData);
          console.log("custom-gwp reconcile:", gwpItems, rawCartData);
          this.#handleStoreGwp(gwpItems, skipCheckout);
          this.#renderGwpInCartDrawer();
        } catch (error) {
          console.error("custom-gwp reconcile failed:", error);
        }
        return rawCartData;
      }
    }
  );
}
